import classNames from 'classnames';
import { noop } from 'lodash';
import {
  ChangeEventHandler,
  createRef,
  FocusEventHandler,
  KeyboardEventHandler,
  PureComponent,
} from 'react';
import { InputDisabled } from '../input-disabled';
import './index.scss';

interface Props {
  id: string;
  disabled: boolean;
  focusOnMount: boolean;
  maxLength: number;
  placeholder: string;
  rows?: number;
  value?: string | number;
  autoFocus?: boolean;
  invalid?: boolean;
  autoresize?: boolean;
  autoresizeDelta?: string;
  onChange: ChangeEventHandler<HTMLTextAreaElement>;
  onBlur?: FocusEventHandler<HTMLTextAreaElement>;
  onFocus?: FocusEventHandler<HTMLTextAreaElement>;
  onKeyDown?: KeyboardEventHandler<HTMLTextAreaElement>;
}

export class Textarea extends PureComponent<Props> {
  public static demoProps = [
    { id: 'text1', placeholder: 'Enter a value' },
    { id: 'text5', placeholder: 'Enter a value', maxLength: 5 },
    { id: 'text2', placeholder: 'Enter a value', disabled: true },
    {
      id: 'text3',
      placeholder: 'Enter a value',
      value: 'Text value',
      disabled: true,
    },
    { id: 'text4', placeholder: 'Focused field', autoFocus: true },
  ];

  public static demoHandlers = {
    onChange: {
      prop: 'value',
      handler: (event) => event.target.value,
    },
  };

  public static defaultProps = {
    disabled: false,
    focusOnMount: false,
    maxLength: null,
    placeholder: '',
    invalid: false,
    autoresize: false,
    autoresizeDelta: '0px',
  };

  private inputRef = createRef<HTMLTextAreaElement>();

  public componentDidMount() {
    const { focusOnMount, disabled } = this.props;
    if (focusOnMount && !disabled && this.inputRef.current) {
      this.inputRef.current.focus();
    }
    if (this.props.autoresize) {
      this.resizeTextarea();
    }
  }

  public componentDidUpdate() {
    if (this.props.autoresize) {
      this.resizeTextarea();
    }
  }

  private resizeTextarea() {
    if (this.inputRef.current) {
      this.inputRef.current.style.height = '1px';
      this.inputRef.current.style.height = `calc(${this.inputRef.current.scrollHeight}px + ${this.props.autoresizeDelta})`;
    }
  }

  public render() {
    const {
      disabled,
      id,
      onChange,
      onBlur,
      onFocus,
      onKeyDown,
      placeholder,
      rows,
      autoFocus,
      value,
      maxLength,
      invalid,
    } = this.props;

    const props = {
      id,
      ref: 'field',
      placeholder,
      className: classNames('InputTextarea__input', 'form-control', {
        'InputTextarea__input--invalid': invalid,
      }),
      onBlur,
      onFocus,
      onChange,
      rows,
      style: rows ? { height: 'inherit' } : undefined,
      value: value === null ? '' : value,
      disabled,
      autoFocus,
      onKeyDown: onKeyDown || noop,
      maxLength: maxLength !== null ? maxLength : undefined,
    };

    return (
      <div className="InputTextarea InputField InputField__input">
        {disabled ? (
          <InputDisabled id={id} value={value} />
        ) : (
          <textarea {...props} ref={this.inputRef} />
        )}
      </div>
    );
  }
}
